import { DEFAULT_SEARCH_SORT, VALID_SORTS } from "@app/constants";
import SearchLayout from "@app/layouts/Search/SearchLayout";
import { TeaserDataApiResponse } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import { FetchError } from "@app/utils/swr";
import MetaData from "@components/Modules/MetaData";
import PubBaseLayout from "@pub/BaseLayout";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import {
  fetchContext,
  ResponseType,
  TRedirectResponse,
  TRouteWithoutRedirect,
} from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { useEffect, useState } from "react";
import {
  LoaderFunctionArgs,
  MetaArgs,
  useLocation,
  useNavigate,
} from "react-router";
import useSWRInfinite from "swr/infinite";

export const meta = ({ location }: MetaArgs) => {
  const params = new URLSearchParams(location.search);
  const searchTerm = params.get("q") || "";

  const pageTitle = searchTerm ? `You searched for ${searchTerm}` : "Search";
  const canonicalPath = searchTerm
    ? `/search?q=${encodeURIComponent(searchTerm)}`
    : "/search";

  return MetaData({
    title: pageTitle,
    type: "CollectionPage",
    path: canonicalPath,
  });
};

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [searchTerm, setSearchTerm] = useState(() => {
    return params.get("q") || "";
  });

  const [searchSetting, setSearchSetting] = useState(() => {
    const sortQuery = params.get("sort");
    return sortQuery && VALID_SORTS.includes(sortQuery)
      ? sortQuery
      : DEFAULT_SEARCH_SORT;
  });

  const handleDropdownChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newSearchSetting = event.target.value;
    setSearchSetting(newSearchSetting);
    void navigate(
      `?q=${encodeURIComponent(searchTerm)}${newSearchSetting === DEFAULT_SEARCH_SORT ? "" : `&sort=${encodeURIComponent(newSearchSetting)}`}`,
    );
  };

  const getSearchUrl = (size?: number) => {
    if (!searchTerm) return;
    const params = [
      "size=10",
      `q=${encodeURIComponent(searchTerm)}`,
      searchSetting === DEFAULT_SEARCH_SORT
        ? ""
        : `sort=${encodeURIComponent(searchSetting)}`,
      size && `after=${encodeURIComponent(size)}`,
    ]
      .filter(Boolean)
      .join("&");
    return `/_plat/api/teaser/search?${params}`;
  };

  const { data, isLoading, error, size, setSize } = useSWRInfinite<
    TeaserDataApiResponse,
    FetchError
  >(getSearchUrl, {
    revalidateFirstPage: false,
    initialSize: searchTerm ? 1 : 0,
  });

  const firstData = data?.[0];
  const hasMoreData = !!data?.[size - 1]?.endCursor;
  const isLoadingMore = size > 1 && typeof data?.[size - 1] === "undefined";
  const loadMore = hasMoreData
    ? () => {
        void setSize((size) => (size += 1));
      }
    : undefined;

  useEffect(() => {
    if (searchTerm) window.postMessage("search-reset");
  }, [searchTerm]);

  useEffect(() => {
    if (firstData) {
      window.postMessage("search-result");
    }
  }, [firstData]);

  return (
    <PubBaseLayout hasMagNewsletter={firstData?.results?.length === 0}>
      <AdSettings
        pageType="search"
        sectionName="Search"
        adUnits="lb1,lb2,imu1,imu2,catfish,prestitial"
        pageTitle="Search"
        currentLocation={location}
      />

      <SearchLayout
        onSearch={(fieldValue) => {
          setSearchTerm(fieldValue);
          //lets update the url without having to load the page
          if (searchSetting === DEFAULT_SEARCH_SORT) {
            void navigate(`?q=${encodeURIComponent(fieldValue)}`);
          } else {
            void navigate(
              `?q=${encodeURIComponent(fieldValue)}&sort=${encodeURIComponent(searchSetting)}`,
            );
          }
        }}
        searchTerm={searchTerm}
        searchSetting={searchSetting}
        updateSearchSetting={handleDropdownChange}
        isLoading={isLoading}
        error={error}
        data={data}
        loadMore={loadMore}
        isLoadingMore={isLoadingMore}
      />
    </PubBaseLayout>
  );
}

// A "dummy" loader is needed as a fallback in-case the clientLoader(which fetches context.json) fails
export async function loader(): Promise<
  | TRouteWithoutRedirect<GlobalBaseData, GlobalBaseData | string>
  | TRedirectResponse
> {
  const dummyResponse: TRouteWithoutRedirect<
    GlobalBaseData,
    GlobalBaseData | string
  > = {
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: {},
  };

  return await dataLoader({
    type: dummyResponse.type,
    dataSource: dummyResponse,
  });
}

/**
 * Client-only loader to prevent /search.data endpoint requests.
 *
 * React Router's default behavior on client-side navigation (via useNavigate):
 * - Attempts to fetch data from /search.data endpoint
 * - Results in 404 errors (endpoint doesn't exist)
 *
 * By exporting clientLoader:
 * - No requests are made to /search.data endpoint
 * - Data returned from /search.context.json
 * - Component handles all data fetching via useSWRInfinite
 */
export async function clientLoader({
  ...args
}: LoaderFunctionArgs & {
  serverLoader: <T = unknown>() => Promise<T>;
}) {
  const result = await fetchContext(args);

  if (
    !result ||
    typeof result !== "object" ||
    !("context" in result) ||
    !result.context
  ) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  return result;
}
